import type { InvestmentAggregationMetrics } from "@/domain/dashboard";
import type { InvestmentWithDetailsResponse } from "@/domain/investment";
import { isObject } from "../cache/cacheSlots";
import type { AggregateDelta } from "../cache/aggregateSlots";
import { INVESTMENT_STATE_ORDER } from "./investmentRules";

type Row = InvestmentWithDetailsResponse;
type StateAggregation = InvestmentAggregationMetrics["by_state"][number];

const CENTS = 100;
const cents = (value: string | number) => Math.round(Number(value) * CENTS);
const decimal = (value: number) => (value / CENTS).toFixed(2);

function moved(byState: readonly StateAggregation[], row: Row, sign: 1 | -1): StateAggregation[] {
  const { state, amount } = row.investment;
  const current = byState.find((entry) => entry.state === state);
  const count = (current?.count ?? 0) + sign;
  const total = decimal(cents(current?.total_amount ?? 0) + sign * cents(amount));
  const others = byState.filter((entry) => entry.state !== state);
  const next = count > 0 ? [...others, { state, count, total_amount: total }] : others;
  return next.sort(
    (a, b) => INVESTMENT_STATE_ORDER.indexOf(a.state) - INVESTMENT_STATE_ORDER.indexOf(b.state),
  );
}

/**
 * The board's per-state counts and sums (`/dashboard/investments?group_by=state`):
 * a state appears only while it has investments, in the backend's state order.
 */
export const pipelineMetrics = {
  isData: (value: unknown): value is InvestmentAggregationMetrics =>
    isObject(value) &&
    Array.isArray(value.by_state) &&
    value.by_beneficiary == null &&
    value.by_investor == null,
  apply: (data: InvestmentAggregationMetrics, delta: AggregateDelta<Row>) => {
    let byState = data.by_state;
    let count = data.total_count;
    let amount = cents(data.total_amount);
    if (delta.removed) {
      byState = moved(byState, delta.removed, -1);
      count -= 1;
      amount -= cents(delta.removed.investment.amount);
    }
    if (delta.added) {
      byState = moved(byState, delta.added, 1);
      count += 1;
      amount += cents(delta.added.investment.amount);
    }
    return { ...data, by_state: byState, total_count: count, total_amount: decimal(amount) };
  },
};
